import type { DashboardFilters, Paginated, PermissionCode } from '@oxxean/types';
import type {
  AuditEntry,
  AnalyticsParameter,
  RoleItem,
  SettingsResponse,
  UserInput,
  UserListItem,
} from '../../types/domain';
import type { TableState } from '../../hooks/useTableState';
import { http } from '../http';
import { filtersToParams, tableStateToParams } from './params';

// ─── Usuarios ───────────────────────────────────────────────────────────────

export async function fetchUsers(state: TableState): Promise<Paginated<UserListItem>> {
  const response = await http.get<Paginated<UserListItem>>('/users', {
    params: tableStateToParams(state),
  });
  return response.data;
}

export async function createUser(payload: UserInput): Promise<UserListItem> {
  const response = await http.post<UserListItem>('/users', payload);
  return response.data;
}

export async function updateUser(id: string, payload: UserInput): Promise<UserListItem> {
  const response = await http.put<UserListItem>(`/users/${id}`, payload);
  return response.data;
}

export async function setUserActive(id: string, active: boolean): Promise<void> {
  await http.patch(`/users/${id}/active`, { active });
}

/** La contraseña temporal la genera y entrega el backend; el frontend no la fabrica. */
export async function forcePasswordReset(id: string): Promise<void> {
  await http.post(`/users/${id}/force-password-reset`);
}

// ─── Roles ──────────────────────────────────────────────────────────────────

export async function fetchRoles(): Promise<RoleItem[]> {
  const response = await http.get<RoleItem[]>('/roles');
  return response.data;
}

export async function updateRolePermissions(
  code: string,
  permissions: PermissionCode[],
): Promise<RoleItem> {
  const response = await http.put<RoleItem>(`/roles/${code}/permissions`, { permissions });
  return response.data;
}

// ─── Auditoría ──────────────────────────────────────────────────────────────

export async function fetchAudit(
  filters: DashboardFilters,
  state: TableState,
): Promise<Paginated<AuditEntry>> {
  const response = await http.get<Paginated<AuditEntry>>('/audit', {
    params: { ...filtersToParams(filters), ...tableStateToParams(state) },
  });
  return response.data;
}

// ─── Configuración ──────────────────────────────────────────────────────────

export async function fetchSettings(): Promise<SettingsResponse> {
  const response = await http.get<SettingsResponse>('/settings');
  return response.data;
}

export interface ParameterUpdate {
  greenThreshold: number | null;
  yellowThreshold: number | null;
  lowerIsBetter: boolean;
  note: string | null;
}

export async function updateParameter(
  id: string,
  payload: ParameterUpdate,
): Promise<AnalyticsParameter> {
  const response = await http.put<AnalyticsParameter>(`/settings/parameters/${id}`, payload);
  return response.data;
}
